import { useEffect, useRef, useState } from 'react';
import { XIcon } from '../../components/icons';
import { useLang } from '../../lib/language';
import { readRecentLineNotes, rememberLineNote } from './recentLineNotes';

/**
 * "No onions" — the per-item note, typed at the counter.
 *
 * This is the till's half of the note the QR-menu customer already types for
 * themselves. It lands in exactly the same place: `pos_order_lines.notes`, the
 * kitchen ticket (printed under the item name), the KDS card, and the customer
 * receipt when the tenant has IsPrintCustomOptions on.
 *
 * Prop-driven rather than routed through the `ui` modal store: the note belongs
 * to one cart line, and the store's modal payload only carries a catalog item.
 *
 * The chips are this terminal's recently-typed notes — see `recentLineNotes`.
 * Tapping one REPLACES the text rather than appending, because that is what a
 * one-tap chip has to mean for it to be faster than typing; a note that needs
 * two phrases is typed.
 */
export function LineNoteModal({
  itemName,
  note,
  onSave,
  onClose,
}: {
  itemName: string;
  /** The line's current note, or null when it has none yet. */
  note: string | null;
  /** Called with the new note — null clears it. The modal does not close itself. */
  onSave: (note: string | null) => void;
  onClose: () => void;
}) {
  const { t } = useLang();
  const [text, setText] = useState(note ?? '');
  const [recent] = useState(readRecentLineNotes);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Focus with the caret at the END of an existing note, so re-opening a line
  // to add "…and extra sauce" doesn't make the cashier tap into the field.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  function commit(value: string) {
    const clean = value.trim();
    if (clean !== '') rememberLineNote(clean);
    onSave(clean === '' ? null : clean);
    onClose();
  }

  return (
    <div className="modal-overlay show" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <div
            className="modal-header-img"
            style={{ background: 'var(--primary-light)', color: 'var(--accent)' }}
          >
            📝
          </div>
          <div className="modal-header-title">
            <div className="modal-header-name">
              {t({ en: 'Note', ar: 'ملاحظة' })}
            </div>
            <div className="modal-header-name-ar">{itemName}</div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label={t({ en: 'Close', ar: 'إغلاق' })}
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <textarea
            ref={inputRef}
            className="kitchen-note"
            value={text}
            maxLength={500}
            onChange={(e) => setText(e.target.value)}
            // Enter saves (the fast path at a counter); Shift+Enter is a newline.
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                commit(text);
              }
            }}
            placeholder={t({ en: 'Add a note for this item', ar: 'أضف ملاحظة على هذا الصنف' })}
          />

          {recent.length > 0 && (
            <div>
              <div className="mod-group-title" style={{ marginBottom: 6 }}>
                {t({ en: 'Recently used', ar: 'استُخدمت مؤخرًا' })}
              </div>
              <div className="line-note-chips">
                {recent.map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={`line-note-chip${r === text.trim() ? ' is-active' : ''}`}
                    onClick={() => {
                      setText(r);
                      inputRef.current?.focus();
                    }}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <div>
            {note && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  onSave(null);
                  onClose();
                }}
              >
                {t({ en: 'Remove note', ar: 'حذف الملاحظة' })}
              </button>
            )}
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              {t({ en: 'Cancel', ar: 'إلغاء' })}
            </button>
            <button type="button" className="btn-primary-modal" onClick={() => commit(text)}>
              {t({ en: 'Save note', ar: 'حفظ الملاحظة' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
